import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getMenu } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile } from '../services/authService';

function Header() {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const containerRef = useRef(null);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { currentUser, logout } = useAuth();

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedInsideHeader = containerRef.current?.contains(event.target);
      const clickedInsideMobileMenu = mobileMenuRef.current?.contains(event.target);

      if (!clickedInsideHeader && !clickedInsideMobileMenu) {
        setActiveDropdown(null);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch menu data
  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setLoading(true);
        const menuData = await getMenu();
        
        if (!menuData || !Array.isArray(menuData) || menuData.length === 0) {
          setError('No menu items found');
          setLoading(false);
          return;
        }
        
        setMenu(menuData);
        setLoading(false);
      } catch (error) {
        setError('Error loading menu');
        setLoading(false);
      }
    };
    
    fetchMenu();
  }, []);

  // Fetch user profile data if logged in
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (currentUser && currentUser.token) {
        try {
          const profileData = await getUserProfile();
          setUserProfile(profileData);
          console.log('Fetched user profile:', profileData);
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
      }
    };
    
    fetchUserProfile();
  }, [currentUser]);

  // Use the most up-to-date user data
  const userData = userProfile || currentUser || {};
  
  // Extract user initials from profile or current user data
  const getUserInitials = () => {
    const firstName = userData.first_name || userData.firstName || '';
    const lastName = userData.last_name || userData.lastName || '';
    
    if (firstName || lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    }
    return 'U';
  };

  // Detect mobile view
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 992);
    };
    
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);

  const handleDropdownToggle = (dropdown) => {
    if (isMobile) {
      setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
    } else {
      // For desktop, toggle the dropdown
      setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
    }
  };

  const handleMouseEnter = (dropdown) => {
    if (!isMobile) {
      setActiveDropdown(dropdown);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      // Only close if we're not clicking inside the dropdown menu itself
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setActiveDropdown(null);
      }
    }
  };

  const isDropdownActive = (dropdown) => activeDropdown === dropdown;
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    setActiveDropdown(null);
  };

  // Parse SVG string to render it safely
  const renderSVG = (svgString) => {
    if (!svgString) return '🔹';
    return <span dangerouslySetInnerHTML={{ __html: svgString }} />;
  };

  const handleLogout = async () => {
    try {
      await logout();
      setActiveDropdown(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  return (
    <>
    <header>
      <div className="container" ref={containerRef}>
        <div className="logo">
          <Link to="/">
            <span className="logo-text">rkube</span>
          </Link>
        </div>
        
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}
        
        {/* Desktop Navigation */}
        <nav className="nav-links">
          {/* Menu Dropdown */}
          {menu && (
            <div 
              className="dropdown-wrapper"
              onMouseEnter={() => handleMouseEnter('services')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className={`dropdown-toggle ${isDropdownActive('services') ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('services')}
              >
                Services
                <svg className="dropdown-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              
              <div 
                className={`dropdown-menu ${isDropdownActive('services') ? 'active' : ''}`}
                ref={dropdownRef}
              >
                <div className="dropdown-sections">
                  <div className="services-grid">
                    {menu.map((mainItem) => (
                      <div key={mainItem.id} className="dropdown-item">
                        <Link 
                          to={`${mainItem.path}`} 
                          className="dropdown-link"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <div className="dropdown-item-icon purple">
                            {renderSVG(mainItem.icon)}
                          </div>
                          <div className="dropdown-item-content">
                            <h4>{mainItem.title}</h4>
                            <p>{mainItem.description}</p>
                          </div>
                        </Link>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {loading && <div className="loading-indicator">Loading...</div>}
          
          {/* <Link to="/pricing" className="nav-link">Pricing</Link> */}
          <Link to="/filings" className="nav-link">Filings</Link>
          
          <div 
            className="dropdown-wrapper"
            onMouseEnter={() => handleMouseEnter('about')}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              className={`dropdown-toggle ${isDropdownActive('about') ? 'active' : ''}`}
              onClick={() => handleDropdownToggle('about')}
            >
              About
              <svg className="dropdown-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div className={`dropdown-menu ${isDropdownActive('about') ? 'active' : ''}`}>
              <div className="dropdown-section">
                <div className="dropdown-content">
                  <div className="dropdown-item">
                    <Link to="/about" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                      <div className="dropdown-item-content">
                        <h4>About Us</h4>
                        <p>Learn about our mission and vision</p>
                      </div>
                    </Link>
                  </div>
                  <div className="dropdown-item">
                    <Link to="/careers" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                      <div className="dropdown-item-content">
                        <h4>Careers</h4>
                        <p>Join our growing team</p>
                      </div>
                    </Link>
                  </div>
                  <div className="dropdown-item">
                    <Link to="/contact" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                      <div className="dropdown-item-content">
                        <h4>Contact</h4>
                        <p>Reach out to our support team</p>
                      </div>
                    </Link>
                  </div>
                  <div className="dropdown-item">
                    <Link to="/blog" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                      <div className="dropdown-item-content">
                        <h4>Blog</h4>
                        <p>Read our latest insights and updates</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </nav>
        
        {/* Auth Buttons or User Profile */}
        {currentUser ? (
          <div className="header-actions">
            <a href="tel:984-234-7030" className="btn btn-phone">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              {!isMobile ? '(984) 234-7030' : 'Free Consultation'}
            </a>
            <div 
              className="dropdown-wrapper user-profile"
              onMouseEnter={() => handleMouseEnter('profile')}
              onMouseLeave={handleMouseLeave}
            >
              <button 
                className={`dropdown-toggle profile-toggle ${isDropdownActive('profile') ? 'active' : ''}`}
                onClick={() => handleDropdownToggle('profile')}
              >
                <div className="initials-avatar">
                  {getUserInitials()}
                </div>
                <svg className="dropdown-arrow" xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </button>
              <div className={`dropdown-menu profile-dropdown ${isDropdownActive('profile') ? 'active' : ''}`}>
                <div className="dropdown-section profile-section">
                  <div className="profile-header">
                    <div className="initials-avatar large">
                      {getUserInitials()}
                    </div>
                    <div className="profile-info">
                      <h3>{userData.first_name || userData.firstName} {userData.last_name || userData.lastName}</h3>
                      <p>{userData.email}</p>
                    </div>
                  </div>
                  <div className="dropdown-content profile-content">
                    <div className="dropdown-item">
                      <Link to="/profile" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                          </svg>
                        </div>
                        <div className="dropdown-item-content">
                          <h4>My Profile</h4>
                          <p>View and manage your account</p>
                        </div>
                      </Link>
                    </div>
                    {/* Conditionally render Admin menu item */}
                    {userData && userData.user_type === 99 && (
                      <div className="dropdown-item">
                        <Link
                          to="/admin"
                          className="dropdown-link"
                          onClick={() => setActiveDropdown(null)}
                        >
                          <div className="dropdown-item-icon">
                            {/* Admin Icon - Using an icon representing administration or control */}
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="3" y1="9" x2="21" y2="9"></line>
                              <line x1="9" y1="21" x2="9" y2="9"></line>
                            </svg>
                          </div>
                          <div className="dropdown-item-content">
                            <h4>Admin</h4>
                            <p>Manage the platform settings</p>
                          </div>
                        </Link>
                      </div>
                    )}
                    <div className="dropdown-item">
                      <Link to="/dashboard" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="7" height="7"></rect>
                            <rect x="14" y="3" width="7" height="7"></rect>
                            <rect x="14" y="14" width="7" height="7"></rect>
                            <rect x="3" y="14" width="7" height="7"></rect>
                          </svg>
                        </div>
                        <div className="dropdown-item-content">
                          <h4>Dashboard</h4>
                          <p>Access files, forms, and deliverables</p>
                        </div>
                      </Link>
                    </div>
                    <div className="dropdown-item">
                      <Link to="/profile#subscriptions" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2v20M2 12h20"></path>
                          </svg>
                        </div>
                        <div className="dropdown-item-content">
                          <h4>Subscriptions</h4>
                          <p>Manage your services</p>
                        </div>
                      </Link>
                    </div>
                    <div className="dropdown-item">
                      <Link to="/profile#purchase-history" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                            <line x1="3" y1="6" x2="21" y2="6"></line>
                            <path d="M16 10a4 4 0 0 1-8 0"></path>
                          </svg>
                        </div>
                        <div className="dropdown-item-content">
                          <h4>Purchase History</h4>
                          <p>View your past purchases</p>
                        </div>
                      </Link>
                    </div>
                    <div className="dropdown-item">
                      <Link to="/settings" className="dropdown-link" onClick={() => setActiveDropdown(null)}>
                        <div className="dropdown-item-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="3"></circle>
                            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                          </svg>
                        </div>
                        <div className="dropdown-item-content">
                          <h4>Settings</h4>
                          <p>Update your preferences</p>
                        </div>
                      </Link>
                    </div>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-item">
                      <button className="dropdown-link logout-link" onClick={handleLogout}>
                        <div className="dropdown-item-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                          </svg>
                        </div>
                        <div className="dropdown-item-content">
                          <h4>Sign Out</h4>
                          <p>Log out of your account</p>
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="auth-buttons">
            <a href="tel:984-234-7030" className="btn btn-phone">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
              </svg>
              {!isMobile ? '(984) 234-7030' : 'Free Consultation'}
            </a>
            <Link to={`/signin?redirect=${encodeURIComponent(window.location.pathname)}`} className="btn btn-outline">Sign in</Link>
            {/* <button className="btn btn-primary">Book a demo</button> */}
          </div>
        )}
        
        {/* Mobile Menu Toggle */}
        <button className="mobile-menu-toggle" onClick={toggleMobileMenu} aria-label="Toggle menu">
          <div className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </div>
        </button>
      </div>
      
      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'active' : ''}`} ref={mobileMenuRef}>
        <div className="services-grid">
          {menu && menu.map((mainItem) => (
            <div key={mainItem.id} className="dropdown-item">
              <Link to={`${mainItem.path}`} className="dropdown-link" onClick={() => setMobileMenuOpen(false)}>
                <div className="dropdown-item-icon purple">
                  {renderSVG(mainItem.icon)}
                </div>
                <div className="dropdown-item-content">
                  <h4>{mainItem.title}</h4>
                  <p>{mainItem.description}</p>
                </div>
              </Link>
            </div>
          ))}
        </div>
        <Link to="/filings" className="mobile-nav-link" onClick={() => setMobileMenuOpen(false)}>Filings</Link>
        <div className="mobile-nav-section">
          <button 
            className={`mobile-nav-dropdown ${isDropdownActive('about-mobile') ? 'active' : ''}`}
            onClick={() => handleDropdownToggle('about-mobile')}
          >
            About
            <svg className={`dropdown-arrow ${isDropdownActive('about-mobile') ? 'active' : ''}`} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
          <div className={`mobile-dropdown-content ${isDropdownActive('about-mobile') ? 'active' : ''}`}>
            <div className="mobile-dropdown-item">
              <Link to="/about" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                <div className="dropdown-item-content">
                  <h4>Our Story</h4>
                  <p>Learn about our mission and vision</p>
                </div>
              </Link>
            </div>
            <div className="mobile-dropdown-item">
              <Link to="/careers" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                <div className="dropdown-item-content">
                  <h4>Careers</h4>
                  <p>Join our growing team</p>
                </div>
              </Link>
            </div>
            <div className="mobile-dropdown-item">
              <Link to="/contact" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                <div className="dropdown-item-content">
                  <h4>Contact</h4>
                  <p>Reach out to our support team</p>
                </div>
              </Link>
            </div>
            <div className="mobile-dropdown-item">
              <Link to="/blog" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                <div className="dropdown-item-content">
                  <h4>Blog</h4>
                  <p>Read our latest insights and updates</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
        
        {/* Mobile Auth Buttons or Profile */}
        {currentUser ? (
          <div className="mobile-nav-section">
            <button 
              className={`mobile-nav-dropdown ${isDropdownActive('profile-mobile') ? 'active' : ''}`}
              onClick={() => handleDropdownToggle('profile-mobile')}
            >
              <div className="mobile-nav-profile">
                <div className="initials-avatar">{getUserInitials()}</div>
                <span>{userData.first_name || userData.firstName} {userData.last_name || userData.lastName}</span>
              </div>
              <svg className={`dropdown-arrow ${isDropdownActive('profile-mobile') ? 'active' : ''}`} xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div className={`mobile-dropdown-content ${isDropdownActive('profile-mobile') ? 'active' : ''}`}>
              <div className="mobile-dropdown-item">
                <Link to="/profile" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                  <div className="dropdown-item-content">
                    <h4>Profile</h4>
                    <p>Manage your account</p>
                  </div>
                </Link>
              </div>
              <div className="mobile-dropdown-item">
                <Link to="/settings" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                  <div className="dropdown-item-content">
                    <h4>Settings</h4>
                    <p>Update your preferences</p>
                  </div>
                </Link>
              </div>
              <div className="mobile-dropdown-item">
                <Link to="/dashboard" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                  <div className="dropdown-item-content">
                    <h4>Dashboard</h4>
                    <p>Access files and deliverables</p>
                  </div>
                </Link>
              </div>
              <div className="mobile-dropdown-item">
                <Link to="/profile#subscriptions" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                  <div className="dropdown-item-content">
                    <h4>Subscriptions</h4>
                    <p>Manage your services</p>
                  </div>
                </Link>
              </div>
              <div className="mobile-dropdown-item">
                <Link to="/profile#purchase-history" className="dropdown-link" onClick={() => {setMobileMenuOpen(false); setActiveDropdown(null);}}>
                  <div className="dropdown-item-content">
                    <h4>Purchase History</h4>
                    <p>View your past purchases</p>
                  </div>
                </Link>
              </div>
              <div className="mobile-dropdown-item">
                <button className="dropdown-link" onClick={() => {handleLogout(); setMobileMenuOpen(false);}}>
                  <div className="dropdown-item-content">
                    <h4>Sign Out</h4>
                    <p>Log out of your account</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mobile-auth-buttons">
            <Link to={`/signin?redirect=${encodeURIComponent(window.location.pathname)}`} className="btn btn-outline" onClick={() => setMobileMenuOpen(false)}>Sign in</Link>
            <button className="btn btn-primary">Book a demo</button>
          </div>
        )}
      </div>
    </header>
    
    {/* Mobile Consultation Bar - appears below header on mobile */}
    <div className="mobile-consultation-bar">
      <a href="tel:984-234-7030" className="btn btn-phone-mobile">
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
        </svg>
        Free Consultation
      </a>
    </div>
    </>
  );
}

export default Header;